import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { 
  initializePushNotifications, 
  linkDeviceToUser, 
  unlinkDeviceFromUser 
} from '@/lib/pushNotifications';

type AppRole = 'admin' | 'staff' | 'user';

// NOTE: Admin bootstrap is now handled server-side via database trigger
// for security. See auto_assign_admin_role() trigger on auth.users table.

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  roles: AppRole[];
  rolesLoaded: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  isAdminOrStaff: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, username?: string, gender?: string, phone?: string, marketingConsent?: boolean, termsAccepted?: boolean) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshRoles: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  // Track which session IDs have already had roles fetched to avoid double-fetch on mount
  const fetchedSessionRef = React.useRef<string | null>(null);

  const fetchUserRoles = async (userId: string): Promise<AppRole[]> => {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);
    
    if (error) {
      logger.error('Error fetching roles', { userId });
      return [];
    }
    
    return (data || []).map(r => r.role as AppRole);
  };

  const refreshRoles = useCallback(async () => {
    if (!user) return;
    
    const fetchedRoles = await fetchUserRoles(user.id);
    setRoles(fetchedRoles);
  }, [user]);

  useEffect(() => {
    // Defer push notifications initialization to improve cold start performance
    const pushTimer = setTimeout(() => {
      initializePushNotifications();
    }, 2500); // 2.5 seconds after app start
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Skip role fetch if we already fetched for this session (avoids double-fetch on mount)
          if (fetchedSessionRef.current === session.access_token) {
            setLoading(false);
            return;
          }
          setRolesLoaded(false);
          setTimeout(async () => {
            const fetchedRoles = await fetchUserRoles(session.user.id);
            setRoles(fetchedRoles);
            fetchedSessionRef.current = session.access_token;
            setRolesLoaded(true);
            linkDeviceToUser();
          }, 0);
        } else {
          setRoles([]);
          fetchedSessionRef.current = null;
          setRolesLoaded(true);
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        // Check if account was soft-deleted while session was still alive
        const { data: profile } = await supabase
          .from('profiles')
          .select('is_deleted')
          .eq('id', session.user.id)
          .single();

        if (profile?.is_deleted) {
          await supabase.auth.signOut();
          setSession(null);
          setUser(null);
          setRoles([]);
          setRolesLoaded(true);
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session.user);
        setRolesLoaded(false);
        const fetchedRoles = await fetchUserRoles(session.user.id);
        setRoles(fetchedRoles);
        // Mark this session as fetched so onAuthStateChange skips the duplicate
        fetchedSessionRef.current = session.access_token;
        setRolesLoaded(true);
        linkDeviceToUser();
      } else {
        setSession(null);
        setUser(null);
        setRolesLoaded(true);
      }

      setLoading(false);
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(pushTimer);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error, data } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) return { error };

    // Block soft-deleted accounts
    const { data: profile } = await supabase
      .from('profiles')
      .select('is_deleted')
      .eq('id', data.user.id)
      .single();

    if (profile?.is_deleted) {
      await supabase.auth.signOut();
      return { error: new Error('account_deleted') };
    }

    return { error: null };
  };

  const signUp = async (email: string, password: string, username?: string, gender?: string, phone?: string, marketingConsent?: boolean, termsAccepted?: boolean) => {
    const redirectUrl = `${window.location.origin}/`;

    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          username: username || email.split('@')[0],
          ...(gender ? { gender } : {}),
          ...(phone ? { phone } : {}),
          marketing_consent: marketingConsent ? 'true' : 'false',
          terms_accepted: termsAccepted ? 'true' : 'false',
        },
      },
    });
    
    // Send custom welcome email after successful signup
    if (!error && data.user) {
      try {
        await supabase.functions.invoke('send-auth-email', {
          body: {
            type: 'signup',
            email,
            username: username || email.split('@')[0],
            redirect_to: redirectUrl,
          },
        });
      } catch (emailError) {
        // Don't fail signup if email fails - just log it
        logger.error('Failed to send welcome email', { email });
      }
    }
    
    return { error };
  };

  const signOut = async () => {
    // Unlink device from push notifications
    unlinkDeviceFromUser();
    
    // Clear local state first
    setUser(null);
    setSession(null);
    setRoles([]);
    
    // Then attempt server-side logout (ignore errors if session already expired)
    try {
      await supabase.auth.signOut();
    } catch (error) {
      // Session may already be expired/invalid on server - this is fine
      logger.info('Logout completed (session may have been expired)');
    }
  };

  const isAdmin = roles.includes('admin');
  const isStaff = roles.includes('staff');
  const isAdminOrStaff = isAdmin || isStaff;

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        roles,
        rolesLoaded,
        isAdmin,
        isStaff,
        isAdminOrStaff,
        signIn,
        signUp,
        signOut,
        refreshRoles,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // During HMR/hot reload, context may briefly be undefined
    // Return a safe default to prevent crashes
    console.warn('useAuth called outside AuthProvider - returning loading state');
    return {
      user: null,
      session: null,
      loading: true,
      roles: [] as AppRole[],
      rolesLoaded: false,
      isAdmin: false,
      isStaff: false,
      isAdminOrStaff: false,
      signIn: async () => ({ error: new Error('AuthProvider not mounted') }),
      signUp: async () => ({ error: new Error('AuthProvider not mounted') }),
      signOut: async () => {},
      refreshRoles: async () => {},
    };
  }
  return context;
}
